import React, { FunctionComponent, useRef, useEffect } from 'react'
import { Script } from 'react-static-render/components/script'

type SvgProps = {
  size?: number,
  strokeWidth?: number
}

export const SvgPlus: FunctionComponent<SvgProps> = (props) => {
  const strokeWidth = props.strokeWidth ?? 1
  const svgRef = useRef<SVGSVGElement>(null)

  const fn = (strokeWidth: number, svg = document.currentScript?.previousElementSibling) => {
    if(!svg) return
    const update = () => {
      const height = svg.getBoundingClientRect().height
      const size = height
      svg.setAttribute('viewBox', `0 0 ${size} ${size}`)
      svg.setAttribute('width', String(size))
      svg.setAttribute('height', String(size))
      svg.innerHTML = `<path d="M0 ${size / 2}L${size} ${size / 2}M ${size / 2} 0L${size / 2} ${size}" fill="none" stroke="currentColor" stroke-width="${strokeWidth}"/>`
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(svg)
  }

  useEffect(() => { fn(strokeWidth, svgRef.current ?? undefined) }, [strokeWidth])

  return <>
    <svg ref={svgRef}/>
    <Script fn={fn} args={[strokeWidth]} waitLoad={false}/>
  </>
}

export const SvgMinus: FunctionComponent<SvgProps> = (props) => {
  const strokeWidth = props.strokeWidth ?? 1
  const svgRef = useRef<SVGSVGElement>(null)

  const fn = (strokeWidth: number, svg = document.currentScript?.previousElementSibling) => {
    if(!svg) return
    const update = () => {
      const height = svg.getBoundingClientRect().height
      const size = height
      svg.setAttribute('viewBox', `0 0 ${size} ${size}`)
      svg.setAttribute('width', String(size))
      svg.setAttribute('height', String(size))
      svg.innerHTML = `<path d="M0 ${size / 2}L${size} ${size / 2}" fill="none" stroke="currentColor" stroke-width="${strokeWidth}"/>`
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(svg)
  }

  useEffect(() => { fn(strokeWidth, svgRef.current ?? undefined) }, [strokeWidth])

  return <>
    <svg ref={svgRef}/>
    <Script fn={fn} args={[strokeWidth]} waitLoad={false}/>
  </>
}

export const SvgCross: FunctionComponent<SvgProps> = (props) => {
  const strokeWidth = props.strokeWidth ?? 1
  const svgRef = useRef<SVGSVGElement>(null)

  const fn = (strokeWidth: number, svg = document.currentScript?.previousElementSibling) => {
    if(!svg) return
    const update = () => {
      const height = svg.getBoundingClientRect().height
      const size = height
      const sin = Math.sin(Math.PI / 4) * size / 2
      svg.setAttribute('viewBox', `0 0 ${size} ${size}`)
      svg.setAttribute('width', String(size))
      svg.setAttribute('height', String(size))
      svg.innerHTML = `<path d="M${size / 2 - sin} ${size / 2 - sin}L${size / 2 + sin} ${size / 2 + sin}M${size / 2 + sin} ${size / 2 - sin}L${size / 2 - sin} ${size / 2 + sin}" fill="none" stroke="currentColor" stroke-width="${strokeWidth}"/>`
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(svg)
  }

  useEffect(() => { fn(strokeWidth, svgRef.current ?? undefined) }, [strokeWidth])

  return <>
    <svg ref={svgRef}/>
    <Script fn={fn} args={[strokeWidth]} waitLoad={false}/>
  </>
}

export const SvgCircle: FunctionComponent<SvgProps> = (props) => {
  const strokeWidth = props.strokeWidth ?? 1
  const svgRef = useRef<SVGSVGElement>(null)

  const fn = (strokeWidth: number, svg = document.currentScript?.previousElementSibling) => {
    if(!svg) return
    const update = () => {
      const height = svg.getBoundingClientRect().height
      const size = height
      const r = size / 2
      svg.setAttribute('viewBox', `0 0 ${size} ${size}`)
      svg.setAttribute('width', String(size))
      svg.setAttribute('height', String(size))
      svg.innerHTML = `<circle cx="${size / 2}" cy="${size / 2}" r="${r - strokeWidth / 2}" fill="none" stroke="currentColor" stroke-width="${strokeWidth}"/>`
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(svg)
  }

  useEffect(() => { fn(strokeWidth, svgRef.current ?? undefined) }, [strokeWidth])

  return <>
    <svg ref={svgRef}/>
    <Script fn={fn} args={[strokeWidth]} waitLoad={false}/>
  </>
}

export const SvgLeftAngle: FunctionComponent<SvgProps> = (props) => {
  const strokeWidth = props.strokeWidth ?? 1
  const svgRef = useRef<SVGSVGElement>(null)

  const fn = (strokeWidth: number, svg = document.currentScript?.previousElementSibling) => {
    if(!svg) return
    const update = () => {
      const height = svg.getBoundingClientRect().height
      const size = height
      const tipMargin = strokeWidth / 2 * Math.sqrt(2)
      const endMargin = strokeWidth / 2 / Math.tan(Math.PI / 180 * 45)
      const w = (size / 2 - tipMargin) / Math.tan(Math.PI / 180 * 45)
      svg.setAttribute('viewBox', `0 0 ${w + tipMargin + endMargin} ${size}`)
      svg.setAttribute('width', String(w + tipMargin + endMargin))
      svg.setAttribute('height', String(size))
      svg.innerHTML = `<path d="M${w + endMargin} ${endMargin} L${tipMargin} ${size/2}L${w + endMargin} ${size - endMargin}" fill="none" stroke="currentColor" stroke-width="${strokeWidth}"/>`
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(svg)
  }

  useEffect(() => { fn(strokeWidth, svgRef.current ?? undefined) }, [strokeWidth])

  return <>
    <svg ref={svgRef}/>
    <Script fn={fn} args={[strokeWidth]} waitLoad={false}/>
  </>
}

export const SvgRightAngle: FunctionComponent<SvgProps> = (props) => {
  const strokeWidth = props.strokeWidth ?? 1
  const svgRef = useRef<SVGSVGElement>(null)

  const fn = (strokeWidth: number, svg = document.currentScript?.previousElementSibling) => {
    if(!svg) return
    const update = () => {
      const height = svg.getBoundingClientRect().height
      const size = height
      const tipMargin = strokeWidth / 2 * Math.sqrt(2)
      const endMargin = strokeWidth / 2 / Math.tan(Math.PI / 180 * 45)
      const w = (size / 2 - tipMargin) / Math.tan(Math.PI / 180 * 45)
      svg.setAttribute('viewBox', `0 0 ${w + tipMargin + endMargin} ${size}`)
      svg.setAttribute('width', String(w + tipMargin + endMargin))
      svg.setAttribute('height', String(size))
      svg.innerHTML = `<path d="M${endMargin} ${endMargin} L${w + tipMargin} ${size/2}L${endMargin} ${size - endMargin}" fill="none" stroke="currentColor" stroke-width="${strokeWidth}"/>`
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(svg)
  }

  useEffect(() => { fn(strokeWidth, svgRef.current ?? undefined) }, [strokeWidth])

  return <>
    <svg ref={svgRef}/>
    <Script fn={fn} args={[strokeWidth]} waitLoad={false}/>
  </>
}

export const SvgUpAngle: FunctionComponent<SvgProps> = (props) => {
  const strokeWidth = props.strokeWidth ?? 1
  const svgRef = useRef<SVGSVGElement>(null)

  const fn = (strokeWidth: number, svg = document.currentScript?.previousElementSibling) => {
    if(!svg) return
    const update = () => {
      const width = svg.getBoundingClientRect().width
      const size = width
      const tipMargin = strokeWidth / 2 * Math.sqrt(2)
      const endMargin = strokeWidth / 2 / Math.tan(Math.PI / 180 * 45)
      const h = (size / 2 - tipMargin) / Math.tan(Math.PI / 180 * 45)
      svg.setAttribute('viewBox', `0 0 ${size} ${size}`)
      svg.setAttribute('width', String(size))
      svg.setAttribute('height', String(size))
      const verticalOffset = (size - (h + tipMargin + endMargin)) / 2
      svg.innerHTML = `<path d="M${endMargin} ${h + endMargin + verticalOffset} L${size/2} ${tipMargin + verticalOffset}L${size - endMargin} ${h + endMargin + verticalOffset}" fill="none" stroke="currentColor" stroke-width="${strokeWidth}"/>`
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(svg)
  }

  useEffect(() => { fn(strokeWidth, svgRef.current ?? undefined) }, [strokeWidth])

  return <>
    <svg ref={svgRef}/>
    <Script fn={fn} args={[strokeWidth]} waitLoad={false}/>
  </>
}

export const SvgDownAngle: FunctionComponent<SvgProps> = (props) => {
  const strokeWidth = props.strokeWidth ?? 1
  const svgRef = useRef<SVGSVGElement>(null)

  const fn = (strokeWidth: number, svg = document.currentScript?.previousElementSibling) => {
    if(!svg) return
    const update = () => {
      const width = svg.getBoundingClientRect().width
      const size = width
      const tipMargin = strokeWidth / 2 * Math.sqrt(2)
      const endMargin = strokeWidth / 2 / Math.tan(Math.PI / 180 * 45)
      const h = (size / 2 - tipMargin) / Math.tan(Math.PI / 180 * 45)
      svg.setAttribute('viewBox', `0 0 ${size} ${size}`)
      svg.setAttribute('width', String(size))
      svg.setAttribute('height', String(size))
      const verticalOffset = (size - (h + tipMargin + endMargin)) / 2
      svg.innerHTML = `<path d="M${endMargin} ${endMargin + verticalOffset} L${size/2} ${h + tipMargin + verticalOffset}L${size - endMargin} ${endMargin + verticalOffset}" fill="none" stroke="currentColor" stroke-width="${strokeWidth}"/>`
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(svg)
  }

  useEffect(() => { fn(strokeWidth, svgRef.current ?? undefined) }, [strokeWidth])

  return <>
    <svg ref={svgRef}/>
    <Script fn={fn} args={[strokeWidth]} waitLoad={false}/>
  </>
}

type SvgRectProps = {
  strokeWidth?: number
}

export const SvgHorizontalRoundedRect: FunctionComponent<SvgRectProps> = (props) => {
  const strokeWidth = props.strokeWidth ?? 1
  const svgRef = useRef<SVGSVGElement>(null)

  const fn = (strokeWidth: number, svg = document.currentScript?.previousElementSibling) => {
    if(!svg) return
    const update = () => {
      const {width, height} = svg.getBoundingClientRect()
      const strokeMargin = strokeWidth / 2 + 1
      const rectWitdh = width - (strokeMargin * 2)
      const rectHeight = height - (strokeMargin * 2)
      svg.setAttribute('viewBox', `0 0 ${width} ${height}`)
      svg.setAttribute('width', String(width))
      svg.setAttribute('height', String(height))
      svg.innerHTML = `<path d="M${strokeMargin + (rectWitdh / 2)} ${strokeMargin}L${strokeMargin + rectWitdh - (rectHeight / 2)} ${strokeMargin}A${rectHeight / 2} ${rectHeight / 2} 0 0 1 ${strokeMargin + rectWitdh - (rectHeight / 2)} ${strokeMargin + rectHeight}L${strokeMargin + (rectHeight / 2)} ${strokeMargin + rectHeight}A${rectHeight / 2} ${rectHeight / 2} 0 1 1 ${strokeMargin + (rectHeight / 2)} ${strokeMargin}Z" fill="none" stroke="currentColor" stroke-width="${strokeWidth}"/>`
      const path = svg.querySelector('path')
      if(path){
        const length = path.getTotalLength()
        path.style.setProperty('--length', String(length))
      }
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(svg)
  }

  useEffect(() => { fn(strokeWidth, svgRef.current ?? undefined) }, [strokeWidth])

  return <>
    <svg ref={svgRef}/>
    <Script fn={fn} args={[strokeWidth]} waitLoad={false}/>
  </>
}
